var React = require('react');
const { useState, useRef, useEffect } = React;
require('./FieldSelectionRow.css');
var fieldManager = require('fieldManager');
const { MiniGraph } = require('../general/MiniGraph');
const FieldSettingsMenu = require('./FieldSettingsMenu.jsx');

const LazyMiniGraphSection = ({ timeZone, data, latestValue }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const id = requestIdleCallback(() => setIsReady(true));
    return () => cancelIdleCallback(id);
  }, [isVisible]);

  return (
    <div ref={ref} style={{ display: 'flex', width: '40px' }}>
      {isReady ? (
        <>
          <span
            style={{ display: 'inline-block', float: 'left', height: '50px' }}
          >
            <MiniGraph timeZone={timeZone} data={data} />
          </span>
          <span
            className='text-size-14 text-grey45'
            style={{
              marginLeft: '4px',
              marginTop: '12px',
              float: 'right',
            }}
          >
            {latestValue}
          </span>
        </>
      ) : null}
    </div>
  );
};

const FieldSelectionRow = React.memo(
  ({
    fieldData,
    isActive,
    isAdmin,
    isModerator,
    showReportIssue,
    distributionId,
    distributionName,
    farmingUnitId,
    farmingUnitName,
    userName,
    userEmail,
    farmingUnitUserEmails,
    onSelect,
    onDuplicate,
    t,
    // Legacy props (used by ArchiveFieldsSelector)
    onMouseLeave,
    onMouseEnter,
    onClick,
    currentField,
    currentBackground,
    rowContent,
    settingsElement: legacySettingsElement,
    hoveredField,
  }) => {
    // Legacy path for ArchiveFieldsSelector
    if (currentField && !fieldData) {
      return (
        <div
          onMouseLeave={() => onMouseLeave()}
          onMouseEnter={() => onMouseEnter()}
          onClick={() => onClick()}
          className='list-group-item m-0 p-0 pt-1'
          style={{
            borderRadius: '0%',
            border: '0px solid white',
            cursor: 'pointer',
            backgroundColor: currentBackground,
            borderLeft: currentField.isActive ? '6px solid #4D5674' : '',
            borderBottom: '1px solid #EEF1F6',
          }}
        >
          {rowContent}
          {hoveredField === currentField.id ? (
            <div
              className='d-flex flex-row-reverse align-items-center position-absolute'
              style={{
                top: 0,
                right: 0,
                bottom: 0,
                width: '30%',
                background:
                  'linear-gradient(270deg, #F6F8FA 50%, #F6F8FA 50%, rgba(246, 248, 250, 0) 100%)',
              }}
            >
              {legacySettingsElement ? (
                <div className='dropdown'>
                  <div
                    className='mr-3'
                    data-toggle='dropdown'
                    aria-haspopup='true'
                    aria-expanded='false'
                  >
                    <img
                      src={require('images/icons/field_selection/settings-icon.svg')}
                      alt='irrigation ok'
                    />
                  </div>
                  {legacySettingsElement}
                </div>
              ) : null}
            </div>
          ) : (
            ''
          )}
        </div>
      );
    }

    let samples;
    if (fieldData.wpSamples !== undefined) {
      samples = fieldData.wpSamples.map((sample, index) => ({
        value: parseFloat(sample.value),
        date: sample.date,
        index: index,
      }));
    }

    let nameElement = '';
    let wdElement = '';

    if (fieldData.ir == -1.0) {
      nameElement = (
        <div
          className='text-grey45 pr-0 line-clamp justify-content-center mb-1'
          style={{ maxHeight: '2.250em', lineHeight: '110%' }}
        >
          <p className='m-0'>{fieldData.name}</p>
        </div>
      );
    } else {
      // Crop water availability (Low / Medium / High) replaces the soil water
      // deficit number for every planner state, so the v2 stress levels are
      // always used here.
      const stressMsg = fieldManager.get_stress_message(
        fieldData.stressLevel,
        fieldData.ir,
        fieldData.accumulatedETc,
        null,
        fieldData.userLengthUnits,
        true,
      );
      wdElement = (
        <div className='pl-0 pr-3 font-weight-semi-bold text-size-16'>
          <div style={{ marginTop: '-2px' }}>
            <div
              style={{
                backgroundColor: fieldData.stressBackgroundColor,
                color: '#FFFFFF',
                width: 64,
                height: 24,
                textAlign: 'center',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {stressMsg.mainMessage ? t(stressMsg.mainMessage) : ''}
            </div>
          </div>
        </div>
      );
      nameElement = (
        <div
          className='text-grey45 pr-0 line-clamp justify-content-center mb-1'
          style={{ maxHeight: '2.125em', lineHeight: '110%' }}
        >
          <p className='m-0'>{fieldData.name}</p>
        </div>
      );
    }

    let settingsElement = null;
    if (isAdmin || isModerator) {
      settingsElement = (
        <FieldSettingsMenu
          field={{ id: fieldData.id, name: fieldData.name }}
          isAdmin={isAdmin}
          isModerator={isModerator}
          showReportIssue={showReportIssue}
          distributionId={distributionId}
          distributionName={distributionName}
          farmingUnitId={farmingUnitId}
          farmingUnitName={farmingUnitName}
          userName={userName}
          userEmail={userEmail}
          farmingUnitUserEmails={farmingUnitUserEmails}
          onDuplicate={() => onDuplicate(fieldData)}
          trackEvent={(page, data) => {
            mixpanel.track(page, data);
          }}
          t={t}
          className='field-settings-menu'
        />
      );
    }

    return (
      <div
        onClick={() => onSelect(fieldData.id)}
        className='list-group-item m-0 p-0 pt-1 field-selection-row'
        style={{
          borderRadius: '0%',
          border: '0px solid white',
          cursor: 'pointer',
          backgroundColor: isActive ? '#F6F8FA' : 'white',
          borderLeft: isActive ? '6px solid #4D5674' : '',
          borderBottom: '1px solid #EEF1F6',
        }}
      >
        <div
          className='d-flex align-items-center row'
          style={{
            paddingLeft: isActive ? '11px' : '16px',
            height: '64px',
            flexWrap: 'nowrap',
            marginRight: '16px',
          }}
        >
          <div
            className='text-size-16 font-weight-bold mr-auto flex-fill'
            style={{ marginLeft: '16px', marginRight: '14px' }}
          >
            {nameElement}
          </div>
          <>
            <div style={{ marginRight: '11px', marginLeft: '8px' }}>
              {wdElement}
            </div>
            <div className='pl-0 pr-5 text-right text-size-12'>
              <LazyMiniGraphSection
                timeZone={fieldData.timeZone}
                data={samples}
                latestValue={
                  samples && samples.length > 0 && samples[0]?.value != null
                    ? samples[0].value.toFixed(0)
                    : ''
                }
              />
            </div>
          </>
        </div>
        {settingsElement ? (
          <div
            className='field-hover-overlay flex-row-reverse align-items-center position-absolute'
            style={{
              top: 0,
              right: 0,
              bottom: 0,
              width: '30%',
              background:
                'linear-gradient(270deg, #F6F8FA 50%, #F6F8FA 50%, rgba(246, 248, 250, 0) 100%)',
            }}
          >
            <div className='dropdown'>
              <div
                className='mr-3'
                data-toggle='dropdown'
                aria-haspopup='true'
                aria-expanded='false'
              >
                <img
                  src={require('images/icons/field_selection/settings-icon.svg')}
                  alt='settings'
                />
              </div>
              {settingsElement}
            </div>
          </div>
        ) : null}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Legacy path (ArchiveFieldsSelector)
    if (prevProps.currentField) {
      return (
        prevProps.currentField === nextProps.currentField &&
        prevProps.currentBackground === nextProps.currentBackground &&
        prevProps.hoveredField === nextProps.hoveredField
      );
    }
    return (
      prevProps.fieldData === nextProps.fieldData &&
      prevProps.isActive === nextProps.isActive
    );
  },
);

module.exports = FieldSelectionRow;
